import { App, Button, DatePicker, Form, Input, InputNumber, Switch } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type File from '../../../interfaces/File';
import type {
  SetFolderPasswordPayload,
  UpdateFolderPasswordPayload,
} from '../../../interfaces/FoldersApiService';
import { FoldersService } from '../../../services/foldersApiService';
import { useAppStore } from '../../../store/store';
import { resolveFolderPasswordApiErrorMessage } from '../../../utils/folderPasswordApiErrors';
import { handleFilePasswordError } from '../../../utils/filePasswordErrors';
import { dateToTicks, ticksToDayjs } from '../../../utils/utils';

// Enable UTC plugin for dayjs to work with UTC dates.
dayjs.extend(utc);

// Create a folders service instance for API calls.
const foldersService = new FoldersService();

// Props for FolderPasswordForm component.
interface FilePasswordFormProps {
  file: File;
  /** Called on success; receives the new password state and owner bypass (latter only relevant when current user is owner). */
  onSubmit: (hasPassword: boolean, ownerBypass?: boolean) => void;
  onCancel: () => void;
}

// Mode of form, determines what the form is doing.
type FormMode = 'create' | 'edit' | 'remove';

// Props for the custom control used in the form.
interface OwnerBypassControlProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  label: string;
  description: string;
}

// Custom switch (used for owner bypass) with formatted label and description.
const OwnerBypassControl = ({
  checked,
  onChange,
  disabled,
  label,
  description,
}: OwnerBypassControlProps) => (
  <div className="mt-4 flex items-center justify-between">
    <div>
      <div className="font-medium">{label}</div>
      <div className="text-gray-dark text-sm">{description}</div>
    </div>
    <Switch checked={checked} onChange={onChange} disabled={disabled} />
  </div>
);

// Main FolderPasswordForm component.
export const FilePasswordForm = ({ file, onSubmit, onCancel }: FilePasswordFormProps) => {
  const { t } = useTranslation();
  const { notification, message } = App.useApp();
  const removeFilePasswordSession = useAppStore((s) => s.removeFilePasswordSession);
  const [form] = Form.useForm();
  const [mode, setMode] = useState<FormMode>('create');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingInfo, setIsLoadingInfo] = useState(true);

  // In edit mode, Remove and Save are enabled only when current password is entered.
  const currentPasswordValue = Form.useWatch('currentPassword', form);
  const currentPasswordTrimmed =
    typeof currentPasswordValue === 'string' ? currentPasswordValue.trim() : '';
  const canSaveAndRemoveInEdit = mode !== 'edit' || currentPasswordTrimmed.length >= 4;

  // Expiration date: if set, must not be in the past (invalid = disable Save and show red border).
  const expirationAtValue = Form.useWatch('expirationAt', form);
  const isExpirationValid =
    !expirationAtValue ||
    (dayjs.isDayjs(expirationAtValue) &&
      expirationAtValue.isValid() &&
      !expirationAtValue.isBefore(dayjs().startOf('day')));

  /**
   * Handles the submission of the form.
   * 1. Validates user inputs.
   * 2. Prepares API payload based on mode.
   * 3. Calls API to create/update/delete folder password.
   * 4. Shows success notification.
   * 5. Calls onSubmit callback.
   * 6. Handles errors.
   */
  const handleSubmit = useCallback(async () => {
    if (mode === 'remove') {
      return;
    }

    // Validate user inputs.
    const values = await form.validateFields();

    setIsLoading(true);
    try {
      if (mode === 'create') {
        // Prepare API payload for creation.
        const payload: SetFolderPasswordPayload = {
          password: values.password,
          expirationAt: values.expirationAt ? dateToTicks(values.expirationAt.toDate()) : null,
          ownerBypass: values.ownerBypass ?? true,
          //adminBypass: values.adminBypass ?? false,
          maxFailedAttempts: values.maxFailedAttempts ?? 5,
          lockoutDurationMinutes: values.lockoutDurationMinutes ?? 30,
          sessionDurationMinutes: values.sessionDurationMinutes ?? 60,
        };

        // API: set folder password.
        const createResult = await foldersService.createFolderPassword(file.id, payload);
        message.success(t('notifications.folderPasswordSet') || 'Password protection enabled');
        onSubmit(createResult.hasPassword, createResult.ownerBypass);
      } else if (mode === 'edit') {
        // Prepare API payload for update.
        const payload: UpdateFolderPasswordPayload = {
          currentPassword: values.currentPassword || null,
          newPassword: values.newPassword || null,
          expirationAt: values.expirationAt ? dateToTicks(values.expirationAt.toDate()) : null,
          ownerBypass: values.ownerBypass,
          //adminBypass: values.adminBypass,
          maxFailedAttempts: values.maxFailedAttempts,
          lockoutDurationMinutes: values.lockoutDurationMinutes,
          sessionDurationMinutes: values.sessionDurationMinutes,
        };

        // API: update folder password.
        const updateResult = await foldersService.updateFolderPassword(file.id, payload);
        // Clear any existing session for this folder so the user is kicked out immediately.
        removeFilePasswordSession(file.id);
        message.success(t('notifications.folderPasswordUpdated') || 'Password protection updated');
        onSubmit(updateResult.hasPassword, updateResult.ownerBypass);
      }
    } catch (error: unknown) {
      if (handleFilePasswordError(error)) {
        return;
      }
      console.error('Failed to save folder password:', error);
      notification.error({ message: resolveFolderPasswordApiErrorMessage(error, t) });
    } finally {
      setIsLoading(false);
    }
  }, [mode, form, file.id, onSubmit, t, notification, message, removeFilePasswordSession]);

  /**
   * Handles the removal of the folder password protection. Requires current password from form.
   */
  const handleRemove = useCallback(async () => {
    try {
      await form.validateFields(['currentPassword']);
    } catch {
      return; // Validation failed, errors are shown on the form
    }
    const currentPassword = form.getFieldValue('currentPassword')?.trim();
    if (!currentPassword) return;

    setIsLoading(true);
    try {
      await foldersService.deleteFolderPassword(file.id, { currentPassword });
      // Clear any existing session for this folder.
      removeFilePasswordSession(file.id);
      message.success(t('notifications.folderPasswordRemoved') || 'Password protection removed');
      onSubmit(false);
    } catch (error: unknown) {
      if (handleFilePasswordError(error)) {
        return;
      }
      notification.error({ message: resolveFolderPasswordApiErrorMessage(error, t) });
    } finally {
      setIsLoading(false);
    }
  }, [file.id, form, onSubmit, t, notification, message, removeFilePasswordSession]);

  /**
   * When folder changes, fetches its password info and updates the form/mode.
   * 1. Sets loading state.
   * 2. Fetches folder password info.
   * 3. Sets form mode based on presence of password.
   * 4. Fills form with current password settings.
   * 5. Sets default values for creating a password.
   * 6. Handles errors.
   * 7. Sets loading state to false.
   */
  useEffect(() => {
    const loadPasswordInfo = async () => {
      try {
        setIsLoadingInfo(true);
        // Fetch the folder password metadata.
        const info = await foldersService.getFolderPassword(file.id);
        // Set form mode based on presence of password.
        setMode(info.hasPassword ? 'edit' : 'create');

        if (info.hasPassword) {
          // Fill form with current password settings.
          const expirationAtValue = info.expirationAt ? ticksToDayjs(info.expirationAt) : null;
          form.setFieldsValue({
            ownerBypass: info.ownerBypass,
            //adminBypass: info.adminBypass,
            maxFailedAttempts: info.maxFailedAttempts,
            lockoutDurationMinutes: info.lockoutDurationMinutes,
            sessionDurationMinutes: info.sessionDurationMinutes,
            expirationAt: expirationAtValue,
          });
          // Trigger validation for currentPassword so empty field shows as invalid (red border).
          setTimeout(() => {
            form.validateFields(['currentPassword']).catch(() => {});
          }, 0);
          // If expiration date is in the past, trigger validation so field shows as invalid (red border).
          if (
            expirationAtValue &&
            dayjs.isDayjs(expirationAtValue) &&
            expirationAtValue.isBefore(dayjs().startOf('day'))
          ) {
            setTimeout(() => {
              form.validateFields(['expirationAt']).catch(() => {});
            }, 0);
          }
        } else {
          // Set default values for creating a password.
          form.setFieldsValue({
            ownerBypass: true,
            //adminBypass: false,
            maxFailedAttempts: 5,
            lockoutDurationMinutes: 30,
            sessionDurationMinutes: 60,
            expirationAt: null,
          });
        }
      } catch (error: any) {
        // Most errors fall back to create mode with defaults.
        setMode('create');
        form.setFieldsValue({
          ownerBypass: true,
          //adminBypass: false,
          maxFailedAttempts: 5,
          lockoutDurationMinutes: 30,
          sessionDurationMinutes: 60,
          expirationAt: null,
        });
      } finally {
        setIsLoadingInfo(false);
      }
    };
    void loadPasswordInfo();
  }, [file.id, form]);
  return (
    <Form
      form={form}
      layout="vertical"
      size="large"
      className="pt-4! [&_.ant-form-item]:mb-4!"
      onFinish={handleSubmit}
      disabled={isLoading}
    >
      {/* Password input, only shown when creating protection. */}
      {mode === 'create' && (
        <Form.Item
          name="password"
          label={t('modal.filePasswordForm.password')}
          rules={[
            { required: true, message: t('modal.filePasswordForm.validation.required') },
            { min: 4, message: t('modal.filePasswordForm.validation.minLength', { min: 4 }) },
          ]}
        >
          <Input.Password
            disabled={isLoadingInfo || isLoading}
            placeholder={t('modal.filePasswordForm.password')}
            autoFocus
          />
        </Form.Item>
      )}

      {/* Current password (required when editing) and optional new password when editing. */}
      {mode === 'edit' && (
        <>
          <Form.Item
            name="currentPassword"
            label={t('modal.filePasswordForm.currentPassword')}
            validateTrigger={['onChange', 'onBlur']}
            rules={[
              { required: true, message: t('modal.filePasswordForm.validation.required') },
              { min: 4, message: t('modal.filePasswordForm.validation.minLength', { min: 4 }) },
            ]}
          >
            <Input.Password
              placeholder={t('modal.filePasswordForm.currentPassword')}
              disabled={isLoadingInfo || isLoading}
              autoFocus
            />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label={t('modal.filePasswordForm.newPassword')}
            rules={[
              { min: 4, message: t('modal.filePasswordForm.validation.minLength', { min: 4 }) },
            ]}
            help={t('modal.filePasswordForm.newPasswordHelp')}
          >
            <Input.Password
              placeholder={t('modal.filePasswordForm.newPassword')}
              disabled={isLoadingInfo || isLoading}
              autoComplete="new-password"
            />
          </Form.Item>
        </>
      )}

      {/* Owner bypass toggle field, labels and descriptions come from i18n. */}
      <Form.Item name="ownerBypass" valuePropName="checked">
        <OwnerBypassControl
          label={t('modal.filePasswordForm.ownerBypass')}
          description={t('modal.filePasswordForm.ownerBypassDescription')}
          disabled={isLoadingInfo || isLoading}
        />
      </Form.Item>

      {/* Admin bypass toggle (commented for future use). */}
      {/* <Form.Item name="adminBypass" valuePropName="checked">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">{t('modal.filePasswordForm.adminBypass')}</div>
            <div className="text-gray-dark text-sm">
              {t('modal.filePasswordForm.adminBypassDescription')}
            </div>
          </div>
          <Switch disabled={isLoadingInfo || isLoading} />
        </div>
      </Form.Item> */}

      {/* Group for lockout/attempts settings, always shown. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Form.Item
          name="maxFailedAttempts"
          label={t('modal.filePasswordForm.maxFailedAttempts')}
          rules={[{ required: true, message: t('modal.filePasswordForm.validation.required') }]}
          initialValue={5}
          className="w-full!"
        >
          <InputNumber min={1} max={10} className="w-full!" disabled={isLoadingInfo || isLoading} />
        </Form.Item>
        <Form.Item
          name="lockoutDurationMinutes"
          label={t('modal.filePasswordForm.lockoutDuration')}
          rules={[{ required: true, message: t('modal.filePasswordForm.validation.required') }]}
          initialValue={30}
          className="w-full!"
        >
          <InputNumber
            min={1}
            max={1440}
            className="w-full!"
            disabled={isLoadingInfo || isLoading}
          />
        </Form.Item>
      </div>

      {/* Session duration and expiration fields. */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Form.Item
          name="sessionDurationMinutes"
          label={t('modal.filePasswordForm.sessionDuration')}
          rules={[{ required: true, message: t('modal.filePasswordForm.validation.required') }]}
          initialValue={60}
        >
          <InputNumber
            min={1}
            max={1440}
            className="w-full!"
            disabled={isLoadingInfo || isLoading}
          />
        </Form.Item>

        {/* Optional expiration date; if set, must not be in the past (e.g. when loaded in edit mode). */}
        <Form.Item
          name="expirationAt"
          label={t('modal.filePasswordForm.expirationDate')}
          validateTrigger={['onChange', 'onBlur']}
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                const d = dayjs.isDayjs(value) ? value : dayjs(value);
                if (!d.isValid()) return Promise.resolve();
                if (d.isBefore(dayjs().startOf('day'))) {
                  return Promise.reject(
                    new Error(
                      t('modal.filePasswordForm.validation.expirationDateExpired') ||
                        'Expiration date cannot be in the past',
                    ),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <DatePicker
            className="w-full!"
            format="YYYY-MM-DD"
            minDate={dayjs()}
            placeholder={t('modal.filePasswordForm.expirationDatePlaceholder')}
            allowClear
            disabled={isLoadingInfo || isLoading}
          />
        </Form.Item>
      </div>

      {/* Footer row: remove button (if edit mode), cancel, save. In edit mode, Remove and Save require current password. */}
      <div className="mt-6 flex flex-row justify-between gap-2">
        {mode === 'edit' ? (
          <Button
            size="middle"
            type="text"
            danger
            onClick={handleRemove}
            loading={isLoading}
            disabled={!canSaveAndRemoveInEdit || isLoadingInfo}
          >
            {t('modal.filePasswordForm.remove')}
          </Button>
        ) : (
          <div />
        )}

        <div className="flex flex-row items-center gap-2">
          <Button size="middle" onClick={onCancel} disabled={isLoadingInfo || isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            size="middle"
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={
              isLoadingInfo || !isExpirationValid || (mode === 'edit' && !canSaveAndRemoveInEdit)
            }
          >
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Form>
  );
};
